// One-way bridge from the new Auth0 SPA SDK (lib/auth.js) to the legacy
// session store (stores/sessionStore.js).
//
// WHY THIS EXISTS:
//
// The legacy SvelteKit app used express-openid-connect server-side; after
// login, the server would render a page that called sessionStore's
// setSession(userData) with a full MongoDB User record. ~56 page components
// today still read `$isAuthenticated` and `$user` from sessionStore — NOT
// from the new lib/auth.js stores.
//
// The new Auth0 SPA SDK writes only to its OWN stores (lib/auth.js's
// isAuthenticated and user). Without a bridge, every legacy page sees
// `isAuthenticated = false` even after a successful sign-in, and redirects
// the user to /login → infinite "stuck on sign-in" loop.
//
// This bridge:
//   1. After the SDK confirms isAuthenticated, fetches `/api/v1/auth/profile`
//      (which returns the MongoDB User record matching the JWT's `sub`).
//   2. Calls sessionStore.setSession(profile) with the result — which makes
//      every legacy page see the authenticated user immediately.
//   3. If the profile fetch fails (e.g. no MongoDB User record yet, or backend
//      down), falls back to a minimal user derived from the Auth0 SDK's own
//      `user` store, so the UI still renders something.
//   4. On sign-out (isAuthenticated → false), calls clearSession().
//
// CALL IT from App.svelte's onMount, AFTER refreshAuthState() resolves.
//
// LONG-TERM: this bridge is the conservative path. The proper fix is to
// migrate the 56 legacy references to read from lib/auth.js directly and
// delete sessionStore. See PORT-STATUS.md "carryover frontend TODOs".

import { get } from 'svelte/store';
import {
  isAuthenticated as sdkIsAuthenticated,
  user as sdkUser
} from './auth.js';
import { setSession, clearSession } from '../stores/sessionStore.js';
import { authApi } from './api.js';

/**
 * Pulls the current authentication state from the Auth0 SDK, fetches the
 * backend profile if authenticated, and writes the result into the legacy
 * session store. Safe to call multiple times.
 */
export async function syncSessionFromAuth0() {
  const authed = get(sdkIsAuthenticated);

  if (!authed) {
    clearSession();
    return;
  }

  // Authenticated: fetch the DB-backed profile, fall back to SDK user info.
  try {
    const profile = await authApi.profile();
    if (profile) {
      // eslint-disable-next-line no-console
      console.info('[sessionBridge] Loaded profile from /api/v1/auth/profile; populating legacy sessionStore.');
      setSession(profile);
      return;
    }
    throw new Error('Empty profile response');
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data ?? e?.message ?? e;
    // eslint-disable-next-line no-console
    console.warn(
      `[sessionBridge] /api/v1/auth/profile failed (status=${status}); falling back to Auth0 SDK user info. Detail:`,
      detail
    );

    // Fallback: derive a minimal user object from the SDK so the UI renders.
    const sdkUserNow = get(sdkUser);
    if (sdkUserNow) {
      setSession({
        email: sdkUserNow.email,
        firstName: sdkUserNow.given_name,
        lastName: sdkUserNow.family_name,
        name: sdkUserNow.name,
        nickname: sdkUserNow.nickname,
        picture: sdkUserNow.picture,
        auth0Id: sdkUserNow.sub,
        roles: sdkUserNow['https://pdh.io/roles'] || [],
        // No MongoDB id/studyIds/siteIds — legacy pages that depend on those
        // will gracefully fall through to "no data" states. This is the
        // expected behavior when the User record hasn't been seeded yet
        // (see docs/QUICK-START-GUIDE.MD §2 for the mongosh seed).
        _bridgeFallback: true
      });
    } else {
      clearSession();
    }
  }
}
